import { z } from "zod";
import { useLocation } from "wouter";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/ui/form/text-input";
import { Form } from "@/components/ui/form/form";
import { useModalError } from "@/hooks/use-modal-error";
import { useSegment } from "@/hooks/use-segment";
import { useCreateContextPack } from "@/api/studio/contexts/queries";
import { createContextPackSchema } from "@/api/studio/contexts/types/context.type";
import { useEffect, useState } from "react";
import { useModalStore } from "@/stores/use-modal-store";
import { useGetCategories } from "@/api/manage/categories/queries";
import { Loading } from "@/components/fallbacks";
import { dynamicUseCase } from "@/utils/dynamic-use-case";
import { useCaseMapping } from "@/utils/use-case-mapping";
import { UseFormSetValue } from "react-hook-form";
import AddDropzone from "@/assets/context-create-thumbnails/add-dropzone.png";
import SelectUseCase from "@/assets/context-create-thumbnails/select-use-case.png";
import UploadContextImg from "@/assets/context-create-thumbnails/upload-context-img.png";
type Form = z.infer<typeof createContextPackSchema>;

/*
 * CreateContextLibraryPackModal
 * Modal to create a new context pack in the "library"
 */
export const CreateContextLibraryPackModal = () => {
  /* Stores */
  const [, navigate] = useLocation();
  const { setConfig } = useModalStore((state) => state.modalActions);

  /* State */
  const [selectedUseCase, setSelectedUseCase] = useState({ id: "", name: "", category: "" });
  const [createContextView, setCreateContextView] = useState<"info" | "type" | "title">("info");

  /* Queries */
  const { mutateAsync: createContextPack, isPending: isCreateContextPackPending, isError: isCreateContextPackError } = useCreateContextPack();
  const { data: categories, isLoading: isCategoriesLoading, isError: isCategoriesError } = useGetCategories();

  /* Hooks */
  const { close } = useModalError({ error: isCreateContextPackError });
  const { segmentTrackEvent } = useSegment();

  /* Handlers */
  const handleCreate = async ({ name, useCaseId, useCaseName }: Form) => {
    const res = await createContextPack({ name, useCaseId, useCaseName });

    // Segment Tracking
    segmentTrackEvent("Context Pack Created", {
      name: name,
      useCaseId: useCaseId,
      useCaseName: useCaseName,
      origin: "Library",
    });

    // Navigate to the next view
    navigate(`/contexts/${res.pack.id}/context-select`);
    close();
  };

  const handleUsecaseSelect = (useCase: { id: string; name: string; category: string }, setValue: UseFormSetValue<Form>) => {
    setSelectedUseCase(useCase);

    // Set values for hidden inputs on the next view
    setValue("useCaseId", useCase.id);
    setValue("useCaseName", useCase.name);
  };

  /* Side Effects */
  useEffect(() => {
    // Set modal size based on view
    if (createContextView === "type") {
      setConfig({ closeOnClickOutside: false, className: "w-[60vw] max-w-[830px] max-h-[70vh]" });
    } else {
      setConfig({ closeOnClickOutside: false, className: "w-[60vw] max-w-[650px]" });
    }
  }, [createContextView, setConfig]);

  /* Render */
  return (
    <Form schema={createContextPackSchema} onSubmit={handleCreate}>
      {({ register, formState: { errors }, setValue }) => {
        if (createContextView === "info") {
          return (
            <div className="flex flex-col gap-6">
              <div className="flex items-center justify-between pb-3">
                <h2>How to create a new context pack</h2>
                <XMarkIcon onClick={close} className="size-5 cursor-pointer" />
              </div>

              {/* 1. Select a use case */}
              <div className="flex gap-4">
                <img src={SelectUseCase} alt="Two use cases with a third positioned ontop with a selected colour" className="w-28" />
                <div>
                  <h3>1. Select a use case</h3>
                  <p className="opacity-70">Choose the type of asset you will test in this context.</p>
                </div>
              </div>

              {/* 2. Upload context images */}
              <div className="flex gap-4">
                <img src={UploadContextImg} alt="Two images with an upload icon on-top" className="w-28" />
                <div>
                  <h3>2. Upload context images</h3>
                  <p className="opacity-70">You can upload multiple context versions that will form part of the new context pack.</p>
                </div>
              </div>

              {/* 3. Add dropzones */}
              <div className="flex gap-4">
                <img src={AddDropzone} alt="A graphic with a dropzone around the edge" className="w-28" />
                <div>
                  <h3>3. Add dropzones</h3>
                  <p className="opacity-70">Draw an area where your asset would normally be placed in the context environment.</p>
                </div>
              </div>

              {/* Nav buttons */}
              <div className="sticky bottom-0 flex justify-end bg-base-100 pb-6 translate-y-6">
                <Button variant="primary" onClick={() => setCreateContextView("type")}>
                  Continue
                </Button>
              </div>
            </div>
          );
        } else if (createContextView === "type") {
          return (
            <div>
              <div className="flex items-center justify-between pb-6">
                <h2>What type of assets will you test in this context?</h2>
                <XMarkIcon onClick={close} className="size-5 cursor-pointer" />
              </div>
              <p className="opacity-70 pb-6">For example, If you are testing how well your hero image performs in a website, select ‘hero image.’</p>
              {isCategoriesLoading && <Loading />}
              {isCategoriesError && (
                <div className="flex justify-center">
                  <p>Failed to load categories</p>
                </div>
              )}
              {categories && (
                <div>
                  {categories.map((category) => (
                    <div key={category.id} className="pb-6 grid grid-cols-3 gap-4">
                      <h3 className="opacity-60 pb-2 col-span-3">{category.name}</h3>
                      {category.useCases.map((useCase) => (
                        <div
                          key={useCase.id}
                          className={`h-35 border cursor-pointer border-base-300 rounded-2xl p-4 ${selectedUseCase.id === useCase.id ? "bg-" + useCaseMapping[useCase.name]?.color : null}`}
                          onClick={() => handleUsecaseSelect({ id: useCase.id, name: useCase.name, category: useCase.category }, setValue)}
                        >
                          {dynamicUseCase(useCase.name, `size-6 ${selectedUseCase.id === useCase.id ? "text-base-100" : "text-" + useCaseMapping[useCase.name]?.color}`)}
                          <h3 className={`pt-4 ${selectedUseCase.id === useCase.id ? "text-base-100" : ""}`}>{useCase.name}</h3>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
              {/* Nav buttons */}
              <div className="sticky bottom-0 flex justify-end bg-base-100 py-4 translate-y-6">
                <Button variant="primary" disabled={!selectedUseCase.id && !selectedUseCase.name} onClick={() => setCreateContextView("title")}>
                  Continue
                </Button>
              </div>
            </div>
          );
        } else {
          return (
            <>
              <div className="flex items-center justify-between pb-6">
                <h2>Confirm context pack title</h2>
                <XMarkIcon onClick={close} className="size-5 cursor-pointer" />
              </div>

              <p className="opacity-70 pb-6">
                Tip: A great context title might include your market, or marketing channels this context relates to. For example, “Top 10 Retailers in US 2025.”
              </p>

              {/* Title input */}
              <TextInput label="Title" registration={register("name")} error={errors.name?.message} className="w-full" placeholder="Context name" />

              {/* Hidden inputs to save useCases from the previous step */}
              <TextInput type="hidden" registration={register("useCaseId")} error={errors.useCaseId?.message} />
              <TextInput type="hidden" registration={register("useCaseName")} error={errors.useCaseName?.message} />

              {/* Nav buttons */}
              <div className="sticky bottom-0 flex justify-end bg-base-100 py-4 translate-y-6">
                <Button type="button" className="mr-2" onClick={() => setCreateContextView("type")}>
                  Back
                </Button>
                <Button type="submit" variant="primary" disabled={isCreateContextPackPending}>
                  {isCreateContextPackPending ? "Creating..." : "Create context"}
                </Button>
              </div>
            </>
          );
        }
      }}
    </Form>
  );
};
